// @ts-nocheck
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useLazyQuery, useQuery } from '@apollo/client'
import { InView } from 'react-intersection-observer'
import Flatpickr from 'react-flatpickr'
import debounce from 'lodash/debounce'
import { ToastContainer } from 'react-toastify'
import { ChevronDown, ChevronUp, Calendar, Users } from 'lucide-react'
import { CubeLoader } from '@/components/ui/cube-loader'
import { Button } from '@/components/ui/button'
import { GET_FEEDS, GET_FEED_PLATFORMS, GET_FEED_CAMPAIGN_KEYWORDS } from '@/graphql/publisher-dashboard-queries'
import { logos } from '@/utils/platform-logos'
import FeedCard from '@/components/feed/FeedCard'
import FilterOption from '@/components/feed/FilterOption'
import Masonry from '@/components/feed/Masonry'
import ResponsiveMasonry from '@/components/feed/ResponsiveMasonry'
import 'flatpickr/dist/flatpickr.css'

const SENTIMENT_OPTIONS = {
    POSITIVE: 'positive',
    NEUTRAL: 'neutral',
    NEGATIVE: 'negative',
}

const capitaliseFirstLetter = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '')

const SocialFeed = () => {
    const { id } = useParams()
    const [platform, setPlatform] = useState(null)
    const [heights, setHeights] = useState({})
    const [isFullyLoaded, setFullyLoaded] = useState(false)
    const [isOpenFilters, setIsOpenFilters] = useState(false)
    const [keywords, setKeywords] = useState([])
    const [selectedKeywords, setSelectedKeywords] = useState([])
    const [sentiments, setSentiments] = useState([])
    const [filters, setFilters] = useState([])
    const [dateRange, setDateRange] = useState({ from: null, to: null })
    const [deleting, setDeleting] = useState(null)
    const [isPlatformOpen, setIsPlatformOpen] = useState(false)
    const datePickerRef = useRef(null)
    const platformDropdownRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (platformDropdownRef.current && !platformDropdownRef.current.contains(e.target)) {
                setIsPlatformOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const campaign = useQuery(GET_FEED_CAMPAIGN_KEYWORDS, {
        variables: id ? { where: { id } } : {},
    })

    useEffect(() => {
        if (!campaign.loading && campaign?.data?.campaigns) {
            if (campaign.data.campaigns[0]?.keywords !== null) {
                setKeywords(JSON.parse(campaign.data.campaigns[0].keywords))
            }
        }
    }, [campaign])

    const platforms = useQuery(GET_FEED_PLATFORMS, {
        variables: {
            options: { sort: [{ order: 'ASC' }] },
            where: { status: 'ACTIVE' },
        },
        onCompleted: (data) => {
            const first = data.platforms[0]
            setPlatform(first)
            fetchFeeds({
                variables: {
                    where: { platform: { id: first.id }, type: null },
                    options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
                },
            })
        },
    })

    const [fetchFeeds, { data: feedsData, loading: feedsLoading, fetchMore }] = useLazyQuery(GET_FEEDS)

    const onChangePlatform = (p) => {
        setPlatform(p)
        applyFilters(p, dateRange, sentiments, selectedKeywords)
    }

    const onLoadMore = async () => {
        const result = await fetchMore({
            variables: {
                options: {
                    sort: [{ postedAt: 'DESC' }],
                    limit: 18,
                    offset: feedsData?.feeds.length,
                },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev
                return { ...prev, feeds: [...(prev?.feeds ?? []), ...(fetchMoreResult.feeds ?? [])] }
            },
        })
        setFullyLoaded(!result.data.feeds.length)
    }

    const onDeleteFeed = async (id) => {
        setDeleting(id)
        await fetchMore({
            variables: {
                options: { sort: [{ postedAt: 'DESC' }], limit: 0, offset: feedsData?.feeds.length },
            },
            updateQuery: (prev) => ({
                ...prev,
                feeds: prev.feeds.filter((feed) => feed.id !== id),
            }),
        })
        setDeleting(null)
    }

    const handleHeightChange = (index, height) => {
        setHeights((prev) => ({ ...prev, [index]: height }))
    }

    const debouncedApplyFilters = useMemo(
        () =>
            debounce(async (p, dr, sents, kws) => {
                const where = { platform: { id: p?.id }, type: null }
                if (dr.from) where.postedAt_GTE = dr.from.toISOString()
                if (dr.to) where.postedAt_LTE = dr.to.toISOString()
                if (sents.length > 0) where.sentiment_IN = sents
                if (kws.length > 0) where.keywords_SOME = { value_IN: kws }

                setFullyLoaded(false)
                const result = await fetchFeeds({
                    variables: {
                        where,
                        options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
                    },
                })
                setFullyLoaded(!result.data.feeds.length)
            }, 300),
        // fetchFeeds and setFullyLoaded are stable refs — empty deps is intentional
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const applyFilters = (p, dr, sents, kws) => {
        debouncedApplyFilters(p, dr, sents, kws)
    }

    const handleFilterChange = (filter, checked, filterType) => {
        setFilters((prev) => {
            const updated = checked ? [...prev, filter] : prev.filter((f) => f !== filter)

            if (filterType === 'keyword') {
                setSelectedKeywords((prevKws) => {
                    const newKws = checked ? [...prevKws, filter] : prevKws.filter((k) => k !== filter)
                    applyFilters(platform, dateRange, sentiments, newKws)
                    return newKws
                })
            } else if (filterType === 'sentiment') {
                setSentiments((prevSents) => {
                    const val =
                        filter === SENTIMENT_OPTIONS.POSITIVE ? 1 : filter === SENTIMENT_OPTIONS.NEGATIVE ? -1 : 0
                    const newSents = checked ? [...prevSents, val] : prevSents.filter((s) => s !== val)
                    applyFilters(platform, dateRange, newSents, selectedKeywords)
                    return newSents
                })
            }

            if (filter.startsWith('From:')) {
                setDateRange((prev) => {
                    const updated = { ...prev, from: null }
                    applyFilters(platform, updated, sentiments, selectedKeywords)
                    return updated
                })
            } else if (filter.startsWith('To:')) {
                setDateRange((prev) => {
                    const updated = { ...prev, to: null }
                    applyFilters(platform, updated, sentiments, selectedKeywords)
                    return updated
                })
            }

            return updated
        })
    }

    const handleDateRangeChange = (selectedDates) => {
        const from = selectedDates[0] || null
        const to = selectedDates[1] || null
        const newDateRange = { from, to }
        const fromFilter = from ? `From: ${from.toLocaleDateString()}` : null
        const toFilter = to ? `To: ${to.toLocaleDateString()}` : null

        setFilters((prev) => {
            const base = prev.filter((f) => !f.startsWith('From: ') && !f.startsWith('To: '))
            if (fromFilter) base.push(fromFilter)
            if (toFilter) base.push(toFilter)
            return base
        })

        setDateRange(newDateRange)
        applyFilters(platform, newDateRange, sentiments, selectedKeywords)
    }

    const clearAllFilters = async () => {
        setFullyLoaded(false)
        setFilters([])
        setDateRange({ from: null, to: null })
        setSelectedKeywords([])
        setSentiments([])
        const result = await fetchFeeds({
            variables: {
                where: { platform: { id: platform?.id }, type: null },
                options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
            },
        })
        setFullyLoaded(!result.data.feeds.length)
    }

    if (platforms.loading) return <CubeLoader size="default" fullScreen={true} />
    if (platforms.error) {
        return (
            <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-24 tw:text-gray-400">
                <Users size={48} className="tw:mb-3 tw:opacity-30" />
                <p className="tw:text-sm">Failed to load social feed. Please try again later.</p>
            </div>
        )
    }

    return (
        <Fragment>
            <div className="tw:mb-4">
                <div className="card page-card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap page-card-header gap-2">
                        <h5 className="mb-0 tw:flex tw:items-center tw:gap-2">
                            <i className="icon text-gray-200">
                                <Users size={24} />
                            </i>
                            {capitaliseFirstLetter(platform?.type)} Feed
                            <span className="hide-on-mobile"> - {platform?.name}</span>
                            <button className="sm social-btn bg-white rounded-circle p-1 no-pointer tw:border-0">
                                <img
                                    src={logos[platform?.name]}
                                    alt={platform?.name}
                                    className="img-fluid object-contain"
                                    style={{ width: '20px', height: '20px' }}
                                />
                            </button>
                        </h5>
                        <div className="tw:flex tw:flex-row tw:gap-2">
                            <button
                                className="text-secondary tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-blue-300 tw:bg-transparent tw:border-0 tw:cursor-pointer"
                                onClick={() => setIsOpenFilters(!isOpenFilters)}
                            >
                                Filters
                                {isOpenFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>

                            <div className="tw:relative" ref={platformDropdownRef}>
                                <button
                                    className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-blue-300 tw:bg-transparent tw:border-0 tw:cursor-pointer"
                                    onClick={() => setIsPlatformOpen((prev) => !prev)}
                                >
                                    Select Platform <ChevronDown size={16} />
                                </button>
                                {isPlatformOpen && (
                                    <div className="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:bg-[#0a1929] tw:border tw:border-[#1e3a5f] tw:rounded tw:shadow-lg tw:z-50 tw:min-w-32">
                                        {platforms.data?.platforms.map((item) => (
                                            <button
                                                key={item.id}
                                                onClick={() => {
                                                    onChangePlatform(item)
                                                    setIsPlatformOpen(false)
                                                }}
                                                className="tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-3 tw:py-2 tw:text-sm tw:text-left tw:bg-transparent tw:border-0 tw:text-white hover:tw:bg-[#1e3a5f] tw:cursor-pointer"
                                            >
                                                <span
                                                    className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    style={{ width: '20px', height: '20px', flexShrink: 0 }}
                                                >
                                                    <img
                                                        src={logos[item.name]}
                                                        alt={item.name}
                                                        className="img-fluid object-contain"
                                                        style={{ width: '14px', height: '14px' }}
                                                    />
                                                </span>
                                                {item.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button
                                className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-blue-300 tw:bg-transparent tw:border-0 tw:cursor-pointer"
                                onClick={() => datePickerRef.current?.flatpickr?.open()}
                            >
                                <Calendar size={16} />
                                Set Period
                                <Flatpickr
                                    ref={datePickerRef}
                                    options={{ mode: 'range', maxDate: 'today', dateFormat: 'd/m/Y' }}
                                    className="range_flatpicker hidden-input"
                                    value={[dateRange.from, dateRange.to].filter(Boolean)}
                                    onChange={handleDateRangeChange}
                                />
                            </button>
                        </div>
                    </div>
                    <div className="card-body page-card-body">
                        {feedsLoading && <CubeLoader size="default" className="tw:min-h-[300px]" />}
                        {isOpenFilters && (
                            <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4">
                                <div>
                                    <h6 className="tw:mb-2">Keywords</h6>
                                    {keywords.map((keyword, i) => (
                                        <FilterOption
                                            key={i}
                                            uniqueName="keyword"
                                            id={i.toString()}
                                            productName={keyword}
                                            productChecked={selectedKeywords.includes(keyword)}
                                            onChange={(e) => handleFilterChange(keyword, e.target.checked, 'keyword')}
                                        />
                                    ))}
                                </div>
                                <div>
                                    <h6 className="tw:mb-2">Sentiment</h6>
                                    {[
                                        SENTIMENT_OPTIONS.POSITIVE,
                                        SENTIMENT_OPTIONS.NEUTRAL,
                                        SENTIMENT_OPTIONS.NEGATIVE,
                                    ].map((opt) => (
                                        <FilterOption
                                            key={opt}
                                            uniqueName="sentiment"
                                            id={opt}
                                            productName={opt}
                                            productChecked={filters.includes(opt)}
                                            onChange={(e) => handleFilterChange(opt, e.target.checked, 'sentiment')}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:mb-3">
                            {filters.map((filter, i) => (
                                <div
                                    key={i}
                                    className="bg-soft-primary rounded-pill iq-custom-badge tw:flex tw:items-center tw:gap-1"
                                >
                                    {filter}
                                    <button
                                        className="btn btn-primary btn-sm rounded-pill iq-cancel-btn"
                                        onClick={() =>
                                            handleFilterChange(
                                                filter,
                                                false,
                                                filter.includes(SENTIMENT_OPTIONS.POSITIVE) ||
                                                    filter.includes(SENTIMENT_OPTIONS.NEUTRAL) ||
                                                    filter.includes(SENTIMENT_OPTIONS.NEGATIVE)
                                                    ? 'sentiment'
                                                    : 'keyword',
                                            )
                                        }
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                            {filters.length > 1 && (
                                <Button size="sm" onClick={clearAllFilters}>
                                    Clear All
                                </Button>
                            )}
                        </div>

                        {!feedsLoading && feedsData && feedsData.feeds?.length === 0 && (
                            <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:min-h-[500px] tw:text-gray-400">
                                <Users size={48} className="tw:mb-3 tw:opacity-30" />
                                <p className="tw:text-sm">No audience data available for this platform.</p>
                            </div>
                        )}
                        {!feedsLoading && feedsData && feedsData.feeds?.length > 0 && (
                            <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 900: 3, 1200: 4 }}>
                                <Masonry gutter="20px" heights={heights}>
                                    {feedsData.feeds.map((feed, i) => {
                                        const uniqueKey = `${feed.id}-${i}`
                                        return (
                                            <FeedCard
                                                key={uniqueKey}
                                                data={feed}
                                                platform={platform}
                                                index={uniqueKey}
                                                onHeightChange={handleHeightChange}
                                                onDelete={onDeleteFeed}
                                                onDeleting={deleting}
                                            />
                                        )
                                    })}
                                    <InView
                                        onChange={(inView) => {
                                            if (inView) onLoadMore()
                                        }}
                                    />
                                </Masonry>
                            </ResponsiveMasonry>
                        )}
                        {!isFullyLoaded && feedsData && feedsData.feeds?.length > 0 && (
                            <CubeLoader size="default" fullScreen={true} />
                        )}
                    </div>
                </div>
            </div>
            <ToastContainer />
        </Fragment>
    )
}

export default SocialFeed
